import { useSponsorships } from "@/api/sponsorships";
import { useChapterPayments } from "@/api/payments";
import { useState, useMemo } from "react";
import { Receipt, Download, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { currencies } from "@/lib/constants";
import { PaymentReceiptModal } from "@/components/payment/PaymentReceiptModal";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchInput } from "@/components/ui/search-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/* ── Helpers ── */
function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export default function Payments() {
  const { data: sponsorships, isLoading: loadingSponsorships } =
    useSponsorships();
  const { data: chapterPayments, isLoading: loadingPayments } =
    useChapterPayments();
  const [search, setSearch] = useState("");
  const [receiptTarget, setReceiptTarget] = useState(null);

  // Flatten all sponsorship-embedded payments and add direct chapter payments
  // Uses Set for O(1) dedup instead of O(n) .find()
  const allPayments = useMemo(() => {
    const items = [];
    const seenIds = new Set();

    // Add payments from sponsorships
    (sponsorships || []).forEach((sp) => {
      (sp.payments || []).forEach((p) => {
        seenIds.add(p._id);
        const sponsorName = sp.sponsorName;
        const sponsorPhone = sp.sponsorPhone || "";
        const mahalluName = sp.mahalluName || "";
        const place = sp.place || "";
        items.push({
          ...p,
          sponsorName,
          sponsorPhone,
          sponsorshipId: sp.sponsorshipId,
          type: sp.type,
          mahalluName,
          place,
          currency: sp.currency,
          careOf: sp.careOf,
          amountPromised: sp.amountPromised,
          isLinkedToSponsorship: true,
          _search:
            `${sponsorName}\0${p.paymentId}\0${sp.sponsorshipId}\0${mahalluName}\0${place}\0${sponsorPhone}`.toLowerCase(),
          _ts: new Date(p.createdAt).getTime(),
        });
      });
    });

    // Add direct chapter payments (not already in the list from sponsorships)
    (chapterPayments || []).forEach((p) => {
      if (seenIds.has(p._id)) return;
      const sponsorName = p.name || "—";
      const sponsorPhone = p.phone || "";
      const mahalluName = p.mahalluName || "";
      const place = p.place || "";
      items.push({
        ...p,
        sponsorName,
        sponsorPhone,
        sponsorshipId: p.sponsorship?.sponsorshipId || "—",
        type: "Direct",
        mahalluName,
        place,
        currency: p.currency || "INR",
        careOf: p.careOf || null,
        amountPromised: p.amountPromised || 0,
        isLinkedToSponsorship: false,
        _search:
          `${sponsorName}\0${p.paymentId}\0${p.sponsorship?.sponsorshipId || ""}\0${mahalluName}\0${place}\0${sponsorPhone}`.toLowerCase(),
        _ts: new Date(p.createdAt).getTime(),
      });
    });

    items.sort((a, b) => b._ts - a._ts);
    return items;
  }, [sponsorships, chapterPayments]);

  // Search filter — uses pre-computed _search string
  const filtered = useMemo(() => {
    if (!search.trim()) return allPayments;
    const q = search.toLowerCase();
    return allPayments.filter((p) => p._search.includes(q));
  }, [allPayments, search]);

  // Summary
  const summary = useMemo(() => {
    const currencyTotals = {};
    filtered.forEach((p) => {
      const curr = p.currency || "INR";
      currencyTotals[curr] = (currencyTotals[curr] || 0) + (p.amount || 0);
    });
    return { currencyTotals, count: filtered.length };
  }, [filtered]);

  const isLoading = loadingSponsorships || loadingPayments;

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-9 w-full rounded-md" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* ── Header ── */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="px-4 h-14 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold leading-tight">Payments</h1>
            <p className="text-xs text-muted-foreground">
              {summary.count} transactions ·{" "}
              {Object.entries(summary.currencyTotals)
                .map(
                  ([curr, total]) =>
                    `${currencies[curr]?.symbol || curr}${total.toLocaleString()}`,
                )
                .join(" + ")}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 py-2">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, ID, mahallu, or place..."
          />
        </div>
      </div>

      {/* ── Summary Bar ── */}
      <div className="px-4 py-2.5 flex items-center gap-4 sm:gap-6 border-b bg-muted/30 text-xs">
        <div>
          <span className="text-muted-foreground">Transactions:</span>{" "}
          <span className="font-bold tabular-nums">{summary.count}</span>
        </div>
        {Object.entries(summary.currencyTotals).map(([curr, total]) => (
          <div key={curr}>
            <span className="text-muted-foreground">{curr} Total:</span>{" "}
            <span className="font-bold tabular-nums text-emerald-600">
              {currencies[curr]?.symbol || curr}
              {total.toLocaleString()}
            </span>
          </div>
        ))}
      </div>

      {/* ── Table Header ── */}
      <div className="px-3 sm:px-4 py-2 flex items-center gap-2 sm:gap-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b bg-muted/20">
        <span className="w-20 sm:w-24 shrink-0">Payment ID</span>
        <span className="w-20 sm:w-24 shrink-0 hidden lg:block">
          Sponsorship
        </span>
        <span className="flex-1 min-w-0">Sponsor</span>
        <span className="w-20 text-center shrink-0 hidden sm:block">Type</span>
        <span className="w-20 sm:w-28 shrink-0 hidden md:block">
          Mahallu / Place
        </span>
        <span className="w-20 sm:w-24 text-right shrink-0">Amount</span>
        <span className="w-24 sm:w-32 text-right shrink-0 hidden sm:block">
          Date & Time
        </span>
        <span className="w-8 shrink-0"></span>
      </div>

      {/* ── Rows ── */}
      <div className="flex-1">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Receipt className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              {search ? "No matching payments" : "No payments recorded yet"}
            </p>
          </div>
        ) : (
          <Card className="mx-3 sm:mx-4 my-3 overflow-hidden">
            <CardContent className="p-0">
              {filtered.map((p) => {
                const sym = currencies[p.currency || "INR"]?.symbol || "₹";
                const sub = p.type === "Mahallu" ? p.mahalluName : p.place;

                return (
                  <div
                    key={p._id}
                    className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 hover:bg-muted/40 transition-colors border-b last:border-b-0"
                  >
                    {/* Payment ID */}
                    <span className="text-xs font-mono text-muted-foreground w-20 sm:w-24 shrink-0 truncate">
                      #{p.paymentId}
                    </span>

                    {/* Sponsorship ID */}
                    <span className="text-xs font-mono text-muted-foreground w-20 sm:w-24 shrink-0 truncate hidden lg:block">
                      {p.sponsorshipId}
                    </span>

                    {/* Sponsor Name */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {p.sponsorName}
                      </p>
                      {p.sponsorPhone && (
                        <p className="text-[10px] text-muted-foreground truncate flex items-center gap-0.5">
                          <Phone className="h-2.5 w-2.5" />
                          {p.sponsorPhone}
                        </p>
                      )}
                      {p.careOf?.name && (
                        <p className="text-[10px] text-muted-foreground truncate">
                          c/o {p.careOf.name}
                        </p>
                      )}
                    </div>

                    {/* Type */}
                    <div className="w-20 text-center shrink-0 hidden sm:block">
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px]",
                          p.type === "Mahallu"
                            ? "border-violet-200 text-violet-700 bg-violet-50"
                            : "border-sky-200 text-sky-700 bg-sky-50",
                        )}
                      >
                        {p.type}
                      </Badge>
                    </div>

                    {/* Mahallu / Place */}
                    <span className="text-xs text-muted-foreground w-20 sm:w-28 shrink-0 truncate hidden md:block">
                      {sub || "—"}
                    </span>

                    {/* Amount */}
                    <div className="w-20 sm:w-24 text-right shrink-0">
                      <span className="text-sm font-bold tabular-nums text-emerald-600">
                        +{sym}
                        {p.amount?.toLocaleString()}
                      </span>
                    </div>

                    {/* Date & Time */}
                    <div className="w-24 sm:w-32 text-right shrink-0 hidden sm:block">
                      <p className="text-xs text-muted-foreground">
                        {formatDate(p.createdAt)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatTime(p.createdAt)}
                      </p>
                    </div>

                    {/* Receipt Download */}
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                            onClick={() =>
                              setReceiptTarget({
                                paymentId: p.paymentId,
                                name: p.sponsorName,
                                place: p.mahalluName || p.place || "",
                                amount: p.amount,
                                currency: p.currency || "INR",
                                createdAt: p.createdAt,
                                phone: p.phone || "",
                              })
                            }
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          Download Receipt
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Receipt Modal */}
      {receiptTarget && (
        <PaymentReceiptModal
          isOpen={!!receiptTarget}
          onClose={() => setReceiptTarget(null)}
          payment={receiptTarget}
        />
      )}
    </div>
  );
}
